import {
  CONFIG_STATUS_LEVEL,
  CONFIG_TYPES,
  TICKET_STATUS_TEMPLATE,
  TICKET_PRIORITY_TEMPLATE,
  SUPPORT_DEDUPLICATION_TEMPLATE,
  FIELD_MAPPING_TYPE,
} from '../../../../common/constants/integrationConstants/fieldMappingConstants';
import { deepCloneTemplate } from '../../../../common/util/reducerUtils/CrmReducerUtils';
import { ticketMappingStatusOnClear } from '../../../../common/util/SupportIntegrationUtils/ticketMappingUtil';
import {
  createTicketFieldMapping,
  createDeduplicationMapping,
  changeTicketFieldMapping,
  cancelTicketFieldMapping,
  fetchTicketFieldMapping,
  clearTicketFieldMapping,
  removeTicketFieldMapping,
  changeDeduplicationMapping,
  fetchDeduplicationMapping,
  cancelDeduplicationMapping,
} from '../../../routines/integrations/supportIntegrationRoutines';

const initialState = {
  [CONFIG_TYPES.DEDUPLICATION]: {
    ticket: deepCloneTemplate(SUPPORT_DEDUPLICATION_TEMPLATE),
    organization: deepCloneTemplate(SUPPORT_DEDUPLICATION_TEMPLATE),
    account: deepCloneTemplate(SUPPORT_DEDUPLICATION_TEMPLATE),
    contact: deepCloneTemplate(SUPPORT_DEDUPLICATION_TEMPLATE),
  },
  [CONFIG_TYPES.TICKET_FIELD_MAPPING]: {
    ticketStatus: deepCloneTemplate(TICKET_STATUS_TEMPLATE),
    ticketPriority: deepCloneTemplate(TICKET_PRIORITY_TEMPLATE),
  },
  persistedStateTicketFieldMapping: null,
  loading: false,
  error: null,
};

const ticketFieldMappingReducer = (state = initialState, action) => {
  switch (action.type) {
    case changeTicketFieldMapping.TRIGGER: {
      const { velarisStatus, value, fieldMappingType } = action.payload;

      const newTicketMappingState = {
        ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING],
        [fieldMappingType]: {
          ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING][fieldMappingType],
          mapping: {
            ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING][fieldMappingType]
              .mapping,
            [velarisStatus]: [
              ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING][fieldMappingType]
                .mapping[velarisStatus],
              value,
            ],
          },
          status: CONFIG_STATUS_LEVEL.COMPLETED,
        },
      };
      return {
        ...state,
        [CONFIG_TYPES.TICKET_FIELD_MAPPING]: newTicketMappingState,
      };
    }
    case createTicketFieldMapping.TRIGGER:
    case createDeduplicationMapping.TRIGGER:
      return {
        ...state,
        error: null,
      };
    case createTicketFieldMapping.SUCCESS: {
      const { fieldMappingType, mapping } = action.payload;
      const mappingObj = mapping.reduce((acc, { velaris, vendor }) => {
        return { ...acc, [velaris]: vendor };
      }, {});
      const newTicketMappingState = {
        ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING],
        [fieldMappingType]: {
          ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING][fieldMappingType],
          mapping: mappingObj,
          status: CONFIG_STATUS_LEVEL.SAVED,
        },
      };
      return {
        ...state,
        [CONFIG_TYPES.TICKET_FIELD_MAPPING]: newTicketMappingState,
        persistedStateTicketFieldMapping: newTicketMappingState,
        loading: false,
        error: false,
      };
    }
    case createTicketFieldMapping.FAILURE:
    case createDeduplicationMapping.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };

    case cancelTicketFieldMapping.TRIGGER: {
      const { fieldMappingType } = action.payload;
      const previousFieldMapping = JSON.parse(
        JSON.stringify(state.persistedStateTicketFieldMapping[fieldMappingType])
      );
      const newTicketMappingState = {
        ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING],
        [fieldMappingType]: previousFieldMapping,
      };
      return {
        ...state,
        [CONFIG_TYPES.TICKET_FIELD_MAPPING]: newTicketMappingState,
      };
    }
    case fetchTicketFieldMapping.TRIGGER:
    case fetchDeduplicationMapping.TRIGGER:
      return {
        ...state,
        loading: true,
        error: false,
      };
    case fetchTicketFieldMapping.SUCCESS: {
      const { ticketStatus, ticketPriority } = action.payload;
      const newTicketMappingState = {
        ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING],
        [FIELD_MAPPING_TYPE.TICKET_STATUS]: {
          ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING][
            FIELD_MAPPING_TYPE.TICKET_STATUS
          ],
          mapping: ticketStatus,
          status: Object.values(ticketStatus).some((arr) => arr.length > 0)
            ? CONFIG_STATUS_LEVEL.SAVED
            : CONFIG_STATUS_LEVEL.NOT_STARTED,
        },
        [FIELD_MAPPING_TYPE.TICKET_PRIORITY]: {
          ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING][
            FIELD_MAPPING_TYPE.TICKET_PRIORITY
          ],
          mapping: ticketPriority,
          status: Object.values(ticketPriority).some((arr) => arr.length > 0)
            ? CONFIG_STATUS_LEVEL.SAVED
            : CONFIG_STATUS_LEVEL.NOT_STARTED,
        },
      };
      return {
        ...state,
        [CONFIG_TYPES.TICKET_FIELD_MAPPING]: newTicketMappingState,
        persistedStateTicketFieldMapping: newTicketMappingState,
        loading: false,
        error: false,
      };
    }
    case clearTicketFieldMapping.TRIGGER: {
      const { fieldMappingType, velarisStatus } = action.payload;
      const newTicketMappingState = {
        ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING],
        [fieldMappingType]: {
          ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING][fieldMappingType],
          mapping: {
            ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING][fieldMappingType]
              .mapping,
            [velarisStatus]: [],
          },
        },
      };

      newTicketMappingState[fieldMappingType].status =
        ticketMappingStatusOnClear(newTicketMappingState[fieldMappingType]);
      return {
        ...state,
        [CONFIG_TYPES.TICKET_FIELD_MAPPING]: newTicketMappingState,
      };
    }
    case removeTicketFieldMapping.TRIGGER: {
      const { fieldMappingType, velarisStatus, value } = action.payload;
      const newTicketMappingState = {
        ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING],
        [fieldMappingType]: {
          ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING][fieldMappingType],
          mapping: {
            ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING][fieldMappingType]
              .mapping,
            [velarisStatus]: [
              ...state[CONFIG_TYPES.TICKET_FIELD_MAPPING][
                fieldMappingType
              ].mapping[velarisStatus].filter((status) => status !== value),
            ],
          },
        },
      };
      newTicketMappingState[fieldMappingType].status =
        ticketMappingStatusOnClear(newTicketMappingState[fieldMappingType]);
      return {
        ...state,
        [CONFIG_TYPES.TICKET_FIELD_MAPPING]: newTicketMappingState,
      };
    }
    // DEDUPLICATON ACTIONS
    case changeDeduplicationMapping.TRIGGER: {
      const { side, value, entityTab } = action.payload;
      const newDeduplicationState = {
        ...state[CONFIG_TYPES.DEDUPLICATION],
        [entityTab]: {
          ...state[CONFIG_TYPES.DEDUPLICATION][entityTab],
          [side]: value,
        },
      };
      const status =
        newDeduplicationState[entityTab].velaris &&
        newDeduplicationState[entityTab].vendor
          ? CONFIG_STATUS_LEVEL.COMPLETED
          : CONFIG_STATUS_LEVEL.IN_PROGRESS;
      newDeduplicationState[entityTab].status = status;
      return {
        ...state,
        [CONFIG_TYPES.DEDUPLICATION]: newDeduplicationState,
      };
    }
    case cancelDeduplicationMapping.TRIGGER: {
      const previousDeduplicationMapping = JSON.parse(
        JSON.stringify(state.persistedStateDeduplication)
      );
      return {
        ...state,
        [CONFIG_TYPES.DEDUPLICATION]: previousDeduplicationMapping,
      };
    }

    case fetchDeduplicationMapping.SUCCESS: {
      return {
        ...state,
        [CONFIG_TYPES.DEDUPLICATION]: action.payload,
        persistedStateDeduplication: action.payload,
        loading: false,
        error: false,
      };
    }

    case createDeduplicationMapping.SUCCESS: {
      const {
        entity,
        mapping: [{ velaris, vendor }],
      } = action.payload;
      const deduplicationConfig = { ...state[CONFIG_TYPES.DEDUPLICATION] };
      deduplicationConfig[entity] = {
        velaris,
        vendor,
        status: CONFIG_STATUS_LEVEL.SAVED,
      };
      return {
        ...state,
        [CONFIG_TYPES.DEDUPLICATION]: deduplicationConfig,
        persistedStateDeduplication: deduplicationConfig,
        loading: false,
        error: false,
      };
    }
    default:
      return state;
  }
};
export default ticketFieldMappingReducer;
